//—— 生辰欄位的輸入處理 ——
//
//  邊打邊補斜線與冒號、以及「這個日期／時間成不成立」的判斷。
//
//  ⚠️ 同樣一組函式目前在三支產品頁各有一份（bazi-baiye / ziwei-jingyue /
//  match-feihong 的頁面頂端）。這一支是為了個人資料編輯頁抽出來的 ——
//  那三份還沒搬過來，搬的時候要注意它們已經走鐘了（見下面 isBirthTimeValid）。
//
//  為什麼不直接 import 其中一支：它們是寫在頁面檔案裡的模組私有函式，
//  沒有 export，而且頁面彼此不該互相 import。

//「不知道時間」是一個要記錄下來的答案，不是跳過。
//
//  排盤時得知道「這個人沒有時柱」，而不是「這個欄位還沒填」——
//  前者照樣排得出年月日三柱，後者代表資料還沒收完。
//  兩者混在一起的話，沒填的人會被當成沒有時柱，默默少掉一整柱。
export const TIME_UNKNOWN = "unknown";

//收成 YYYY/MM/DD。
//  只取數字再重建，所以使用者打不打斜線都可以，貼上來的格式也不挑。
export const formatBirthDate = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 8);
  return [digits.slice(0, 4), digits.slice(4, 6), digits.slice(6, 8)]
    .filter(Boolean)
    .join("/");
};

//八碼填滿了沒。「成立」是另一回事（見 isBirthDateValid）——
//  分開是為了提示的時機：填到一半就開始糾正很煩人。
export const isBirthDateComplete = (value) =>
  /^\d{4}\/\d{2}\/\d{2}$/.test(value || "");

//這一天真的存在嗎。
//  只檢查格式的話，2 月 30 日和 13 月都會過 ——
//  用 Date 反推回來比對，才擋得住那種「看起來很像日期」的東西。
export const isBirthDateValid = (value) => {
  if (!isBirthDateComplete(value)) return false;

  const [y, m, d] = value.split("/").map(Number);
  if (m < 1 || m > 12 || d < 1) return false;

  const date = new Date(y, m - 1, d);
  return (
    date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d
  );
};

//收成 HH:MM。作法同日期：只取數字再重建
export const formatBirthTime = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 4);
  return [digits.slice(0, 2), digits.slice(2, 4)].filter(Boolean).join(":");
};

export const isBirthTimeComplete = (value) => /^\d{2}:\d{2}$/.test(value || "");

//時間成不成立。
//
//  allowUnknown 預設 true（白夜那一套）：少了時柱還有年月日三柱可以批。
//  紫微與合婚要傳 false —— 它們定命宮要先知道時辰，缺了是整張盤排不出來，
//  十二宮全部跟著錯位，不是「少一段」而已。
export const isBirthTimeValid = (value, { allowUnknown = true } = {}) => {
  if (allowUnknown && value === TIME_UNKNOWN) return true;
  if (!isBirthTimeComplete(value)) return false;

  const [h, m] = value.split(":").map(Number);
  return h <= 23 && m <= 59;
};

//—— 排盤需要的四項齊了沒 ——
//
//  日、時、地、性別，缺一張盤就是另一個人的：
//    日時   四柱的其中兩柱
//    地     真太陽時校正（同一個時鐘時間，台北和喀什的太陽位置差快兩小時）
//    性別   決定大運順行或逆行
//
//  ⚠️ 這份判斷要是全站唯一的。個人資料編輯頁用它顯示「還需要什麼」、
//  提問用它決定「擋不擋」—— 兩邊各寫一份的話，會出現「編輯頁說填好了，
//  提問還是把你擋下來」這種沒人查得出原因的狀況。

//出生地要三項齊全才算數。
//  只有地名沒有座標的話排不出真太陽時，而那種「看起來有填、
//  算出來卻不對」的資料最難查 —— 所以直接當作沒填。
export const normalizeBirthPlace = (raw) =>
  raw?.label && raw.lat != null && raw.lng != null
    ? { label: raw.label, lat: raw.lat, lng: raw.lng }
    : null;

//把 user document 攤成這支檔案在講的那四項。
//  欄位名稱不一樣（user 那邊有 userXxx 前綴），轉接在這裡做一次，
//  呼叫端就不用各自記得對應關係。
export const chartFromUser = (user) => ({
  birthDate: user?.userBirthDate || "",
  birthTime: user?.userBirthTime || "",
  birthPlace: normalizeBirthPlace(user?.userBirthPlace),
  gender: user?.userGender || "",
});

//卜卦只缺性別嗎。
//
//  —— 為什麼不沿用 missingChartFields ——
//  那一支擋的是「排得出盤嗎」，四項缺一不可。但**卜卦不排盤** ——
//  它要的是起卦當下的時間，跟問卦人的生辰完全無關。
//  沿用的話等於要他為了問一卦先填一整份生辰，而那四項裡有三項用不到。
//
//  那為什麼卜卦還是要性別：六爻取用神分男女 ——
//  男占婚姻以妻財為用神（財是男人的妻），女占婚姻以官鬼為用神（官是女人的夫）。
//  不知道誰在問，感情類的卦就會取錯用神，而且錯得看不出來
//  （斷語照樣四平八穩，只是在講另一個人的事）。
//
//  判準跟 missingChartFields 同一條：只認 "0"（女）與 "1"（男）。
//  "2"（其他）與 "" 都算沒填 —— 男女二分是這套術數的前提，
//  放行只會讓它默默取成其中一邊。
export const needsGender = (user) => {
  const g = user?.userGender || "";
  return g !== "0" && g !== "1";
};

//還缺哪幾項，回傳的是給人看的名稱（順序照填寫的順序）。
//  回字串而不是 key：它唯一的用途就是顯示給使用者看，
//  再做一層 key → label 的對照表只是多一個會走鐘的地方。
export const missingChartFields = ({
  birthDate,
  birthTime,
  birthPlace,
  gender,
} = {}) => {
  const list = [];
  //  日期要「成立」不是「有填」—— 2026/02/30 填了也排不出盤
  if (!isBirthDateValid(birthDate)) list.push("出生年月日");
  //  時間允許 unknown：少的是時柱，年月日三柱照樣批得出來
  if (!isBirthTimeValid(birthTime)) list.push("出生時間");
  if (!normalizeBirthPlace(birthPlace)) list.push("出生地");
  //  只認 "0"（女）與 "1"（男）。models/user 的 enum 還留著 "2"（其他），
  //  但排盤的大運順逆只有兩種取法 —— 放行的話會默默排成預設的那一種。
  //  （個人資料頁因此也只給兩個選項，見 GENDERS 那段。）
  if (gender !== "0" && gender !== "1") list.push("性別");
  return list;
};

export const isChartReady = (input) => missingChartFields(input).length === 0;
